<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="smAndDown ? undefined : 560"
    :fullscreen="smAndDown"
    :persistent="submitting"
    scrollable
    @update:model-value="onDialogToggle"
    @after-enter="onAfterEnter"
    @after-leave="onAfterLeave"
  >
    <v-card class="contact-form-card">
      <v-card-title class="contact-form-title">
        <span>{{ copy.title }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          aria-label="Close"
          :disabled="submitting"
          @click="onCancel"
        />
      </v-card-title>

      <v-card-text class="contact-form-body">
        <p v-if="!success" class="contact-form-lede">{{ copy.lede }}</p>

        <div v-if="success" class="contact-form-success" role="status">
          <v-icon class="contact-form-success-icon">mdi-check-circle</v-icon>
          {{ copy.successInline }}
        </div>

        <v-form
          v-else
          ref="formRef"
          @submit.prevent="onSubmit"
        >
          <v-textarea
            ref="messageInputRef"
            v-model="message"
            :label="copy.label"
            :placeholder="copy.placeholder"
            variant="outlined"
            density="comfortable"
            auto-grow
            rows="5"
            max-rows="12"
            counter
            :maxlength="MAX_LENGTH"
            :disabled="submitting"
            :aria-describedby="helperId"
            @keydown="onTextareaKeydown"
          />
          <p :id="helperId" class="contact-form-helper">
            <span v-if="showMinHint" class="contact-form-helper-min">
              Add at least {{ MIN_LENGTH }} characters - {{ remaining }} to go.
            </span>
            <span v-else class="contact-form-helper-ok">
              We usually reply within 48 hours.
            </span>
          </p>
          <p v-if="errorMessage" class="contact-form-error" role="alert">
            {{ errorMessage }}
          </p>
        </v-form>
      </v-card-text>

      <v-card-actions class="contact-form-actions">
        <v-spacer />
        <template v-if="success">
          <v-btn
            color="primary"
            variant="flat"
            class="contact-form-submit-btn"
            @click="onClose"
          >
            Close
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            variant="outlined"
            class="contact-form-cancel-btn"
            :disabled="submitting"
            @click="onCancel"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="contact-form-submit-btn"
            :loading="submitting"
            :disabled="submitting || !canSubmit"
            @click="onSubmit"
          >
            {{ copy.submit }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, nextTick, ref, watch } from "vue";
import { useDisplay } from "vuetify";

// Mirror iOS `ContactViewModel.minimumCharactersForMessage = 35`. iOS uses
// strict `>`, so an actual minimum of 36. We honour the spirit (parity) but
// trim whitespace so a wall of spaces doesn't pass.
const MIN_LENGTH = 35;
const MAX_LENGTH = 2000;

const COPY = {
  feedback: {
    title: "Share feedback",
    lede:
      "What's working, what isn't, or what you'd love to see - we read every message.",
    label: "Your feedback",
    placeholder: "Tell us what's on your mind…",
    submit: "Send feedback",
    successInline: "Thanks - your feedback is on its way.",
    emailTitle: "Feedback request from Web App",
  },
  support: {
    title: "Contact us",
    lede:
      "Tell us what's going on and we'll get back to you by email.",
    label: "How can we help?",
    placeholder: "Describe the issue you're seeing…",
    submit: "Send message",
    successInline: "Thanks - your message is on its way.",
    emailTitle: "Support request from Web App",
  },
  // Sign-up flow: the Established plan is priced on request.
  enquiry: {
    title: "Let's talk about your plan",
    lede: "Our Established plan comes with custom pricing and done-for-you setup. Send us this message and we'll be in touch by email to get you set up.",
    label: "Your message",
    placeholder: "Tell us a little about your business…",
    submit: "Send message",
    successInline:
      "Thanks - your message is on its way. We'll be in touch by email shortly.",
    emailTitle: "Established plan enquiry from Web sign-up",
  },
  // Dashboard billing card: plan changes are handled by the Lulu team, not
  // the Stripe portal.
  plan: {
    title: "Change your plan",
    lede: "Tell us what you'd like to change and we'll sort it out by email.",
    label: "Your message",
    placeholder: "Which plan would you like to move to?",
    submit: "Send message",
    successInline:
      "Thanks - your message is on its way. We'll be in touch by email shortly.",
    emailTitle: "Plan change request from Web App",
  },
};

export default {
  name: "ContactDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    // 'feedback' | 'support' | 'enquiry' | 'plan'
    mode: { type: String, default: "support" },
    // Text the textarea starts with each time the dialog opens.
    initialMessage: { type: String, default: "" },
    submitting: { type: Boolean, default: false },
    errorMessage: { type: String, default: "" },
    success: { type: Boolean, default: false },
    // Element to restore focus to after the dialog closes.
    returnFocusTo: { type: [Object, HTMLElement], default: null },
  },
  emits: ["update:modelValue", "submit", "cancel", "close"],
  setup(props, { emit }) {
    const display = useDisplay();
    const smAndDown = computed(() => display.smAndDown.value);
    const formRef = ref(null);
    const messageInputRef = ref(null);
    const message = ref("");
    const helperId = `contact-form-helper-${Math.random().toString(36).slice(2, 8)}`;

    const copy = computed(() => COPY[props.mode] || COPY.support);

    const trimmedLength = computed(() => message.value.trim().length);
    const canSubmit = computed(() => trimmedLength.value >= MIN_LENGTH);
    const remaining = computed(() =>
      Math.max(0, MIN_LENGTH - trimmedLength.value)
    );
    const showMinHint = computed(() => !canSubmit.value);

    // Reset the textarea every time the dialog opens. Keeping a stale message
    // between sessions would be a privacy footgun (e.g. a shared dashboard
    // machine). `initialMessage` is the parent's prefill, empty by default.
    watch(
      () => props.modelValue,
      (open) => {
        if (open) {
          message.value = props.initialMessage;
        }
      }
    );

    const onAfterEnter = () => {
      nextTick(() => {
        const el = messageInputRef.value?.$el?.querySelector("textarea");
        if (el) el.focus();
      });
    };

    const onAfterLeave = () => {
      const target = props.returnFocusTo;
      if (target && typeof target.focus === "function") {
        try {
          target.focus();
        } catch (e) {
          // Best-effort.
        }
      }
    };

    const onDialogToggle = (open) => {
      if (!open && !props.submitting) emit("update:modelValue", false);
    };

    const onCancel = () => {
      if (props.submitting) return;
      emit("cancel");
      emit("update:modelValue", false);
    };

    const onClose = () => {
      emit("close");
      emit("update:modelValue", false);
    };

    const onSubmit = () => {
      if (!canSubmit.value || props.submitting) return;
      emit("submit", {
        mode: props.mode,
        title: copy.value.emailTitle,
        message: message.value.trim(),
      });
    };

    // Cmd/Ctrl+Enter sends. Plain Enter inserts a newline (default).
    const onTextareaKeydown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        onSubmit();
      }
    };

    return {
      smAndDown,
      formRef,
      messageInputRef,
      message,
      copy,
      canSubmit,
      remaining,
      showMinHint,
      helperId,
      MIN_LENGTH,
      MAX_LENGTH,
      onAfterEnter,
      onAfterLeave,
      onDialogToggle,
      onCancel,
      onClose,
      onSubmit,
      onTextareaKeydown,
    };
  },
};
</script>

<style scoped>
.contact-form-card {
  border-radius: var(--lulu-radius-card);
}

.contact-form-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 18px;
  font-weight: 800;
  color: var(--lulu-text-strong);
  padding: 20px 20px 8px;
}

.contact-form-body {
  padding: 8px 20px 0 !important;
}

.contact-form-lede {
  font-size: 13px;
  color: var(--lulu-text-muted);
  line-height: 1.5;
  margin: 0 0 16px;
}

.contact-form-helper {
  font-size: 12px;
  line-height: 1.5;
  margin: 4px 0 0;
}

.contact-form-helper-min {
  color: var(--lulu-text-muted);
}

.contact-form-helper-ok {
  color: var(--lulu-text-muted);
}

.contact-form-error {
  color: var(--lulu-danger);
  font-size: 13px;
  font-weight: 500;
  margin: 12px 0 0;
}

.contact-form-success {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--lulu-primary-soft);
  color: var(--lulu-primary);
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  margin: 0 0 8px;
}

.contact-form-success-icon {
  color: var(--lulu-primary) !important;
}

.contact-form-actions {
  padding: 12px 20px 16px !important;
}

/* v-dialog teleports outside the parent page's :deep selector, so reset
   text-transform/styling here so action buttons render like other modals. */
.contact-form-card :deep(.v-btn) {
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.contact-form-card :deep(.contact-form-submit-btn) {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 999px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 8px 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.contact-form-card :deep(.contact-form-submit-btn:hover:not(:disabled)) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

.contact-form-card :deep(.contact-form-cancel-btn) {
  color: var(--lulu-primary) !important;
  background: transparent !important;
  border: 1px solid rgba(0, 109, 62, 0.4) !important;
  border-radius: 999px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 8px 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.12) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.contact-form-card :deep(.contact-form-cancel-btn:hover:not(:disabled)) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.2) !important;
  transform: translateY(-1px);
  border-color: var(--lulu-primary) !important;
}
</style>
